import React, { Fragment, useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import SelectMenu from '../../../../component/common/SelectMenu';
import SearchInput from '../../../../component/common/Input/SearchInput';
import { ACCOUNT_TYPE, PER_PAGE } from '../../../../utils/constants';
import { Api } from '../../../../api';
import { errorToast, getFilterKey, useOutsideClick } from '../../../../utils/helper';
import Table from '../../../../component/common/Table';
import { useNavigate, useLocation } from 'react-router-dom';
import Pagination from '../../../../component/common/Pagination/Pagination';
import Breadcrumb from '../../../../component/common/Breadcrumb';
import { FunnelIcon, XMarkIcon } from '@heroicons/react/20/solid';
import { Transition } from '@headlessui/react';
import Loader from '../../../../component/common/Loader';
import Topbar from '../../../PulseSurvey/Topbar';
import { businessHubB2BCompanyListPageData } from '../../../../utils/pageContent';
import { FilterPanel, TableCard} from '../../../../component/common/designSystem';

function Companylist({ topbarShow = true }) {
  const location = useLocation();
  const navigate = useNavigate();
  const wrapperRef = useRef(null);
  const hasInitRef = useRef(false);
  const lastSearchAppliedRef = useRef('');
  const suppressURLSyncRef = useRef(true);

  // Initialize state from URL params synchronously to prevent reset on back navigation
  const getInitialStateFromURL = () => {
    const params = new URLSearchParams(location.search);
    return {
      search: params.get('search') || '',
      sortBy: params.get('sortBy') || '',
      sortOrder: parseInt(params.get('sortOrder')) || -1,
      perPage: parseInt(params.get('perPage')) || 10,
      page: parseInt(params.get('page')) || 1,
      filters: params.get('filters') || ''
    };
  };

  const initialState = getInitialStateFromURL();

  const [loader, setLoader] = useState(false);
  const [usersList, setUsersList] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(initialState.page);
  const [searchTerm, setSearchTerm] = useState(initialState.search);
  const [sortOrder, setSortOrder] = useState(initialState.sortOrder);
  const [sortBy, setSortBy] = useState(initialState.sortBy);
  const [filterQuery, setFilterQuery] = useState(initialState.filters);
  const [selectedPerPage, setSelectedPerPage] = useState(
    initialState.perPage
      ? { label: String(initialState.perPage), value: initialState.perPage }
      : PER_PAGE[0]
  );
  const [showFilterModal, setShowFilterModal] = useState(false);

  // Parse initial filter criteria from URL
  const parseInitialFilters = (filtersStr) => {
    if (!filtersStr) return { companySubscription: '', contractStatus: '' };
    const cleaned = filtersStr.startsWith('&') ? filtersStr.slice(1) : filtersStr;
    const entries = cleaned
      .split('&')
      .filter(Boolean)
      .map((pair) => pair.split('='));
    const criteria = { companySubscription: '', contractStatus: '' };
    const mapValueToOption = (value, options) => {
      const num = isNaN(Number(value)) ? value : Number(value);
      return (
        options.find((o) => o.value === num) || options.find((o) => o.value === value) || value
      );
    };
    entries.forEach(([key, val]) => {
      if (!key) return;
      if (key === 'companySubscription') {
        criteria.companySubscription = mapValueToOption(val, ACCOUNT_TYPE);
      } else if (key === 'contractStatus') {
        criteria.contractStatus = mapValueToOption(
          val,
          businessHubB2BCompanyListPageData.filtersModal.selectMenuTwo.menuList || []
        );
      }
    });
    return criteria;
  };

  const [filterCriteria, setFilterCriteria] = useState(() =>
    parseInitialFilters(initialState.filters)
  );

  const pages = [{ name: 'Company List', href: '/B2B-company-list', current: true }];

  const handlePagination = (pageNumber, pageSize, searchKey, filterQuery, sortBy, sortOrder) => {
    setLoader(true);
    Api.getAllCompanies(pageNumber, pageSize, searchKey, sortBy, sortOrder).then((response) => {
      setLoader(false);
      if (response?.data) {
        if (response?.data?.data) {
          setUsersList(
            response?.data?.data?.map((item) => ({
              ...item,
              name: item?.company_name,
              email: item?.company_email,
              profile: item?.company_logo,
              seat_price: item?.seat_price?.$numberDecimal
                ? parseFloat(item?.seat_price.$numberDecimal).toFixed(2)
                : parseFloat(item?.seat_price || 0).toFixed(2)
            }))
          );
          setCurrentPage(pageNumber);
          setTotalCount(response?.data?.meta?.totalRecords);
          setLoader(false);
        } else {
          setLoader(false);
        }
      } else if (response?.code === 401) {
        errorToast(response?.message);
        setLoader(false);
      } else if (response?.data?.meta?.code === 0) {
        setLoader(false);
        setCurrentPage(1);
        setUsersList([]);
        setTotalCount(0);
        errorToast(response?.data?.meta?.message);
      } else {
        setLoader(false);
      }
    });
  };
  // Query param helpers
  const updateURLParams = ({ search, sortBy, sortOrder, perPage, page, filters }) => {
    const params = new URLSearchParams(location.search);
    if (search !== undefined) params.set('search', search);
    if (sortBy !== undefined) params.set('sortBy', sortBy || '');
    if (sortOrder !== undefined) params.set('sortOrder', String(sortOrder));
    if (perPage !== undefined) params.set('perPage', String(perPage));
    if (page !== undefined) params.set('page', String(page));
    if (filters !== undefined) params.set('filters', filters || '');
    navigate({ pathname: location.pathname, search: params.toString() }, { replace: false });
  };

  const initFromURLParams = () => {
    const params = new URLSearchParams(location.search);
    const urlSearch = params.get('search') || '';
    const urlSortBy = params.get('sortBy') || '';
    const urlSortOrder = parseInt(params.get('sortOrder') || sortOrder || 1);
    const urlPerPage = parseInt(params.get('perPage') || selectedPerPage?.value || 10);
    const urlPage = parseInt(params.get('page') || 1);
    const urlFilters = params.get('filters') || '';
    if (urlSearch !== searchTerm) setSearchTerm(urlSearch);
    if (urlSortBy) setSortBy(urlSortBy);
    if (urlSortOrder !== sortOrder) setSortOrder(urlSortOrder);
    if (urlPerPage !== selectedPerPage?.value)
      setSelectedPerPage({ label: String(urlPerPage), value: urlPerPage });
    if (urlFilters !== filterQuery) setFilterQuery(urlFilters);
    // Hydrate visual filter selections from filters query
    const parsedCriteria = parseFiltersToCriteria(urlFilters);
    if (parsedCriteria) setFilterCriteria(parsedCriteria);
    setCurrentPage(urlPage);

    handlePagination(urlPage, urlPerPage, urlSearch, urlFilters, urlSortBy || sortBy, urlSortOrder);
  };

  // Convert filters query string like "&companySubscription=2&contractStatus=1" to filterCriteria
  const parseFiltersToCriteria = (filtersStr) => {
    if (!filtersStr)
      return {
        companySubscription: '',
        contractStatus: ''
      };
    const cleaned = filtersStr.startsWith('&') ? filtersStr.slice(1) : filtersStr;
    const entries = cleaned
      .split('&')
      .filter(Boolean)
      .map((pair) => pair.split('='));
    const mapValueToOption = (value, options) => {
      const num = isNaN(Number(value)) ? value : Number(value);
      return (
        options.find((o) => o.value === num) || options.find((o) => o.value === value) || value
      );
    };
    const criteria = {
      companySubscription: '',
      contractStatus: ''
    };
    entries.forEach(([key, val]) => {
      if (!key) return;
      switch (key) {
        case 'companySubscription':
          criteria.companySubscription = mapValueToOption(val, ACCOUNT_TYPE);
          break;
        case 'contractStatus':
          criteria.contractStatus = mapValueToOption(
            val,
            businessHubB2BCompanyListPageData.filtersModal.selectMenuTwo.menuList || []
          );
          break;
        default:
          break;
      }
    });
    return criteria;
  };

  useEffect(() => {
    // Avoid duplicate init in StrictMode; only act if search actually changed
    if (location.search === lastSearchAppliedRef.current && hasInitRef.current) return;
    initFromURLParams();
    lastSearchAppliedRef.current = location.search;
    if (!hasInitRef.current) {
      hasInitRef.current = true;
      // Enable URL syncing after first hydration
      suppressURLSyncRef.current = false;
    }
    // Re-init when location.search changes via back/forward
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);
  const handleSortBy = (sortByValue) => {
    setSortBy(sortByValue);
    if (sortByValue === sortBy) {
      let tempSortOrder = sortOrder === 1 ? -1 : 1;
      setSortOrder(tempSortOrder);
      if (!suppressURLSyncRef.current) {
        updateURLParams({
          page: currentPage,
          perPage: selectedPerPage?.value,
          search: searchTerm,
          sortBy: sortByValue,
          sortOrder: tempSortOrder,
          filters: filterQuery
        });
      }
      handlePagination(
        currentPage,
        selectedPerPage?.value,
        searchTerm,
        filterQuery,
        sortByValue,
        tempSortOrder
      );
    } else {
      setSortOrder(1);
      if (!suppressURLSyncRef.current) {
        updateURLParams({
          page: currentPage,
          perPage: selectedPerPage?.value,
          search: searchTerm,
          sortBy: sortByValue,
          sortOrder: 1,
          filters: filterQuery
        });
      }
      handlePagination(
        currentPage,
        selectedPerPage?.value,
        searchTerm,
        filterQuery,
        sortByValue,
        1
      );
    }
  };

  useEffect(() => {
    if (searchTerm) {
      let searchParam = searchTerm.trim();
      const delayDebounceFn = setTimeout(() => {
        if (!suppressURLSyncRef.current) {
          updateURLParams({
            page: 1,
            perPage: selectedPerPage?.value,
            search: searchParam,
            sortBy,
            sortOrder,
            filters: filterQuery
          });
        }
        handlePagination(1, selectedPerPage?.value, searchParam, filterQuery, sortBy, sortOrder);
      }, 800);

      return () => clearTimeout(delayDebounceFn);
    } else if (hasInitRef.current) {
      if (!suppressURLSyncRef.current) {
        updateURLParams({
          page: 1,
          perPage: 10,
          search: '',
          sortBy,
          sortOrder,
          filters: filterQuery
        });
      }
      handlePagination(1, 10, '', filterQuery, sortBy, sortOrder);
    }
  }, [searchTerm]);

  const handlePerPage = (perPage) => {
    setSelectedPerPage(perPage);
    if (!suppressURLSyncRef.current) {
      updateURLParams({
        page: 1,
        perPage: perPage.value,
        search: searchTerm,
        sortBy,
        sortOrder,
        filters: filterQuery
      });
    }
    handlePagination(1, perPage.value, searchTerm, filterQuery, sortBy, sortOrder);
  };

  useOutsideClick(wrapperRef, () => {
    if (showFilterModal) setShowFilterModal(!showFilterModal);
  });

  const refreshTable = () =>
    handlePagination(1, selectedPerPage?.value, '', filterQuery, sortBy, sortOrder);

  const filterHandler = (data, key) => {
    setFilterCriteria((prevData) => ({
      ...prevData,
      [key]: data
    }));
    let clone = JSON.parse(JSON.stringify(filterCriteria));
    const newData = { ...clone, [key]: data };
    let tempFilterQuery = [];
    Object.keys(newData).forEach(function (key) {
      if (newData[key]?.value?.toString()) {
        tempFilterQuery.push(`&${key}=${newData[key]?.value}`);
      }
    });
    let query = tempFilterQuery?.toString()?.replaceAll(',', '');
    setFilterQuery(query);
    if (!suppressURLSyncRef.current) {
      updateURLParams({
        page: 1,
        perPage: selectedPerPage.value,
        search: searchTerm,
        sortBy,
        sortOrder,
        filters: query
      });
    }
    handlePagination(1, selectedPerPage.value, searchTerm, query, sortBy, sortOrder);
  };

  const resetHandler = () => {
    setFilterCriteria({
      companySubscription: ''
    });
    setFilterQuery('');
    setShowFilterModal(false);
    if (!suppressURLSyncRef.current) {
      updateURLParams({
        page: 1,
        perPage: selectedPerPage.value,
        search: searchTerm,
        sortBy,
        sortOrder,
        filters: ''
      });
    }
    handlePagination(1, selectedPerPage.value, searchTerm, '', sortBy, sortOrder);
  };

  return (
    <div className="relative lg:mb-[45px]">
      <Helmet>
        <meta charSet="utf-8" />
        <title>{businessHubB2BCompanyListPageData.metaTitle}</title>
      </Helmet>
      {loader ? <Loader /> : null}
      {topbarShow === true && <Topbar title={businessHubB2BCompanyListPageData.topbarTitleText} />}
      <Breadcrumb pageList={pages} className="lg:mt-8" />

      <div className="mt-6">
        <div className="flex flex-wrap justify-between gap-2">
          <div className="flex">
            <div className="hidden lg:flex mr-3 self-center">
              <span className="self-center text-sm mr-2 text-shoorah-lightBlack dark:text-white">
                {businessHubB2BCompanyListPageData.topbarTitleText}
              </span>
              <div className="w-[80px]">
                <SelectMenu
                  menuList={PER_PAGE}
                  showLabel={false}
                  defaultSelected={selectedPerPage}
                  setSelectedMenu={handlePerPage}
                />
              </div>
            </div>
            <div className="self-center">
              <SearchInput
                id="searchKey"
                value={searchTerm}
                name="searchKey"
                type="text"
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={businessHubB2BCompanyListPageData.searchPlaceholder}
              />
            </div>
          </div>

          <div className="flex flex-wrap sm:gap-0 gap-3 justify-between">
            <div className="flex lg:hidden self-center">
              <div className="w-[80px]">
                <SelectMenu
                  menuList={PER_PAGE}
                  showLabel={false}
                  defaultSelected={selectedPerPage}
                  setSelectedMenu={handlePerPage}
                />
              </div>
            </div>

            <div className="flex flex-wrap">
              <div className="sm:ml-3 self-center inline-flex flex-wrap items-center justify-center">
                <button
                  className="btn-primary sm:w-auto"
                  onClick={() => {
                    navigate('/B2B-company-list/addCompany', {
                      state: { action: 'Add' }
                    });
                  }}
                >
                  {businessHubB2BCompanyListPageData.addCompanyText}
                </button>

                <button
                  className="btn-primary ml-3 sm:w-auto"
                  onClick={() => setShowFilterModal((state) => !state)}
                >
                  <FunnelIcon className="text-white w-[18px] h-[20px] inline" />
                </button>
              </div>
            </div>
          </div>
        </div>

        <FilterPanel
          open={showFilterModal}
          onClose={() => setShowFilterModal(false)}
          onReset={resetHandler}
        >
              <div>
                <SelectMenu
                  menuList={ACCOUNT_TYPE}
                  defaultSelected={filterCriteria.companySubscription}
                  label={businessHubB2BCompanyListPageData.filtersModal.selectMenuOne.label}
                  isRequired={false}
                  setSelectedMenu={(data) => filterHandler(data, 'companySubscription')}
                />
              </div>
              {/* Add more filters as needed */}
              <div>
                <SelectMenu
                  menuList={businessHubB2BCompanyListPageData.filtersModal.selectMenuTwo.menuList}
                  label={businessHubB2BCompanyListPageData.filtersModal.selectMenuTwo.label}
                  isRequired={false}
                  defaultSelected={filterCriteria.contractStatus}
                  setSelectedMenu={(data) => filterHandler(data, 'contractStatus')}
                />
              </div>
            </FilterPanel>

        

        <TableCard
          pagination={{
            currentPage: currentPage,
            totalCount: totalCount,
            pageSize: selectedPerPage?.value,
            onPageChange: (page) => {
                if (!suppressURLSyncRef.current) {
                  updateURLParams({
                    page,
                    perPage: selectedPerPage?.value,
                    search: searchTerm,
                    sortBy,
                    sortOrder,
                    filters: filterQuery
                  });
                }
                handlePagination(
                  page,
                  selectedPerPage?.value,
                  searchTerm,
                  filterQuery,
                  sortBy,
                  sortOrder
                );
              },
            onPageSizeChange: (size) =>
              handlePerPage(PER_PAGE.find((o) => o.value === size) || PER_PAGE[0]),
            pageSizeOptions: PER_PAGE.map((o) => o.value)
          }}
        >
          <Table
            columns={businessHubB2BCompanyListPageData.tableColumns}
            data={usersList}
            name={'companyName'}
            bottomBorder={totalCount > selectedPerPage?.value}
            setSortBy={(sort) => handleSortBy(sort)}
            refreshTable={refreshTable}
            loader={loader}
          />
        </TableCard>

        
      </div>
    </div>
  );
}

export default Companylist;
